import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useFormContext } from 'react-hook-form'

import { Button } from 'components/UI'
import DropZoneAsThumb from './DropZoneAsThumb'

import { getAvatarUrl } from '../../helpers'

import { MAX_UPLOADED_IMAGES } from 'constants/upload'

import NotAccepted from 'assets/icon/cross.png'
import Trash from 'assets/icon/trash.svg'

const Thumbnails = ({ images, remove }) => {
  const [errors, setErrors] = useState([])
  const { setValue } = useFormContext()

  const renderErrors = useMemo(() => {
    return (
      <ul className="mt-1">
        {errors.map(({ file, errors }) => (
          <li key={file.path}>
            <p className="flex gap-2 items-center text-sm text-rose-500 font-semibold">
              <img src={NotAccepted} alt="Not accepted" />
              {file.name}
            </p>
            <ul className="pl-6">
              {errors.map((e) => (
                <li key={e.code} className="text-sm text-rose-500">
                  {e.message}
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    )
  }, [errors])

  const onChange = useCallback(
    (file) => {
      setValue(
        'images',
        images.concat(
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        )
      )
    },
    [images]
  )

  const onError = useCallback((errors) => {
    setErrors(errors)
  }, [])

  const generateImage = (index) => {
    const isImageAvailable = images.length - 1 >= index

    return isImageAvailable ? (
      <div
        key={index}
        className="group relative bg-white flex justify-center items-center w-full aspect-[1/.75] rounded-4 overflow-hidden"
      >
        <img
          src={
            images[index]?.preview ||
            (images[index]?.original ? getAvatarUrl(images[index].original) : '')
          }
          alt="Image thumb"
          className="h-full w-full object-cover"
        />

        <Button
          className="bg-[#F79009] absolute p-2 w-[30px] h-[30px] !rounded-full opacity-0 invisible ease-in transition-all group-hover:!opacity-100 group-hover:!visible"
          onClick={() => remove(index)}
        >
          <img src={Trash} alt="Remove thumb" className="max-h-full object-contain" />
        </Button>
      </div>
    ) : (
      <DropZoneAsThumb key={index} onChange={onChange} onError={onError} />
    )
  }

  const renderTitle = () => {
    if (images.length === 0) {
      return `You can add a maximum of ${MAX_UPLOADED_IMAGES} images`
    } else if (images.length < MAX_UPLOADED_IMAGES) {
      return `${images.length} files (you can add a maximum of ${MAX_UPLOADED_IMAGES} images)`
    }

    return `${MAX_UPLOADED_IMAGES} files (maximum files reached)`
  }

  useEffect(() => {
    setErrors([])
  }, [images])

  return (
    <div className="mt-3">
      <p className="text-[#F67443]">{renderTitle()}</p>

      <div
        style={{ gridTemplateColumns: `repeat(${MAX_UPLOADED_IMAGES}, minmax(0, 1fr))` }}
        className="grid gap-3 mt-2"
      >
        {[]
          .constructor(MAX_UPLOADED_IMAGES)
          .fill(null)
          .map((_, index) => generateImage(index))}
      </div>
      {renderErrors}
    </div>
  )
}

Thumbnails.propTypes = {
  images: PropTypes.array.isRequired,
  remove: PropTypes.func.isRequired,
}

export default React.memo(Thumbnails)
